import React, { useRef, useState, useEffect } from 'react';
import { Camera, MapPin, Loader2, X, Info, Images, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import HintInput from './HintInput';
import PhotoEditModal from './PhotoEditModal';
import TagMultiSelect from './TagMultiSelect';

const label = 'block text-sm font-semibold mb-1';
const input = 'w-full px-3 py-2 rounded border text-sm';
const border = { borderColor: 'rgba(0,0,0,0.2)' };

const readFile = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

// Single file -> edit (resize/rotate/crop) -> details form.
// Multiple files -> batch upload (no crop) -> one shared details form applied to all.
export default function PhotoUploader({ onDone }) {
  const { uploadGalleryPhoto, updateMyPhoto, listMyAlbums, applyPhotoMeta, analyzePhoto } = useAuth();
  const fileRef = useRef(null);
  const [stage, setStage] = useState('idle'); // idle | uploading | form | batchUploading | batchForm
  const [error, setError] = useState('');
  const [photo, setPhoto] = useState(null);
  const [exif, setExif] = useState(null);
  const [preview, setPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [albums, setAlbums] = useState([]);
  const [meta, setMeta] = useState({ title: '', description: '', place: '', album: '', date_taken: '', tags: [], people: '', shared: false, include_location: false });
  const [editSrc, setEditSrc] = useState('');
  const [batchPhotos, setBatchPhotos] = useState([]);
  const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0 });
  const [batchMeta, setBatchMeta] = useState({ album: '', place: '', tags: [], shared: false });
  const [analyzing, setAnalyzing] = useState(false);
  const [aiSuggest, setAiSuggest] = useState(null);

  const runAnalyze = async () => {
    if (!photo) return;
    setAnalyzing(true); setError('');
    try {
      const a = await analyzePhoto(photo.id);
      setAiSuggest(a);
      setMeta((m) => ({
        ...m,
        title: m.title || a.title || '',
        description: m.description || a.description || '',
        tags: Array.from(new Set([...(m.tags || []), ...(a.tags || [])])),
        people: m.people || (a.suggested_members || []).map((s) => s.name).join(', '),
      }));
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.detail) || 'Analysis failed. Please try again.');
    }
    setAnalyzing(false);
  };

  useEffect(() => { listMyAlbums().then(setAlbums).catch(() => setAlbums([])); }, [listMyAlbums]);

  const pick = () => fileRef.current && fileRef.current.click();

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setError('');
    const tooBig = files.find((f) => f.size > 15 * 1024 * 1024);
    if (tooBig) { setError('Each image must be under 15 MB.'); return; }

    if (files.length === 1) {
      const dataUrl = await readFile(files[0]);
      setEditSrc(dataUrl);
      return;
    }
    // Batch: upload all (no per-photo cropping).
    setStage('batchUploading');
    setBatchProgress({ done: 0, total: files.length });
    const uploaded = [];
    try {
      for (let i = 0; i < files.length; i++) {
        const dataUrl = await readFile(files[i]);
        const res = await uploadGalleryPhoto(dataUrl, files[i].type);
        uploaded.push(res.photo);
        setBatchProgress({ done: i + 1, total: files.length });
      }
      setBatchPhotos(uploaded);
      setBatchMeta({ album: '', place: '', tags: [], shared: false });
      setStage('batchForm');
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.detail) || 'Some photos failed to upload.');
      setBatchPhotos(uploaded);
      setStage(uploaded.length ? 'batchForm' : 'idle');
    }
  };

  const uploadEdited = async (dataUrl) => {
    setEditSrc('');
    setPreview(dataUrl);
    setStage('uploading');
    try {
      const res = await uploadGalleryPhoto(dataUrl, 'image/jpeg');
      setPhoto(res.photo);
      setExif(res.exif);
      setMeta((m) => ({ ...m, date_taken: res.exif.taken_at || '' }));
      setStage('form');
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.detail) || 'Upload failed.');
      setStage('idle');
    }
  };

  const save = async () => {
    setSaving(true); setError('');
    try {
      await updateMyPhoto(photo.id, { ...meta, tags: meta.tags });
      reset();
      onDone && onDone();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.detail) || 'Could not save.');
    }
    setSaving(false);
  };

  const saveBatch = async () => {
    setSaving(true); setError('');
    try {
      await applyPhotoMeta(batchPhotos.map((p) => p.id), batchMeta);
      reset();
      onDone && onDone();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.detail) || 'Could not save.');
    }
    setSaving(false);
  };

  const reset = () => {
    setStage('idle'); setPhoto(null); setExif(null); setPreview(''); setBatchPhotos([]);
    setMeta({ title: '', description: '', place: '', album: '', date_taken: '', tags: [], people: '', shared: false, include_location: false });
    setBatchMeta({ album: '', place: '', tags: [], shared: false });
  };

  return (
    <div data-testid="photo-uploader">
      {editSrc && (
        <PhotoEditModal src={editSrc} onCancel={() => setEditSrc('')} onConfirm={uploadEdited} />
      )}
      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onFile} data-testid="photo-file-input" />
      {stage === 'idle' && (
        <button onClick={pick} className="inline-flex items-center gap-2 px-4 py-2 rounded text-sm font-title"
          style={{ background: 'var(--maroon)', color: 'var(--cream)' }} data-testid="photo-pick-btn">
          <Camera size={16} /> Add photos
        </button>
      )}
      {stage === 'idle' && <p className="text-xs mt-1.5" style={{ color: 'var(--olive)' }}>Pick one photo to resize, rotate &amp; crop — or select several to upload as a batch.</p>}
      {error && <p className="text-sm mt-2" style={{ color: '#8a1f11' }} data-testid="photo-error">{error}</p>}

      {stage === 'uploading' && (
        <div className="flex items-center gap-2 text-sm mt-2" style={{ color: 'var(--olive)' }}>
          <Loader2 size={16} className="animate-spin" /> Uploading &amp; reading photo details…
        </div>
      )}

      {stage === 'batchUploading' && (
        <div className="flex items-center gap-2 text-sm mt-2" style={{ color: 'var(--olive)' }} data-testid="batch-uploading">
          <Loader2 size={16} className="animate-spin" /> Uploading {batchProgress.done} of {batchProgress.total} photos…
        </div>
      )}

      {stage === 'batchForm' && batchPhotos.length > 0 && (
        <div className="mt-3 p-4 rounded-lg border" style={{ borderColor: 'rgba(0,0,0,0.12)', background: 'rgba(0,0,0,0.02)' }} data-testid="batch-meta-form">
          <div className="flex items-start justify-between gap-3 mb-3">
            <h4 className="font-title text-sm flex items-center gap-1.5" style={{ color: 'var(--navy)' }}>
              <Images size={15} /> {batchPhotos.length} photos — shared details
            </h4>
            <button onClick={reset} data-testid="batch-cancel"><X size={16} /></button>
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            {batchPhotos.map((p) => (
              <img key={p.id} src={p.url} alt="" className="w-16 h-16 object-cover rounded" style={{ border: '2px solid white' }} data-testid={`batch-thumb-${p.id}`} />
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={label} style={{ color: 'var(--ink)' }}>Album</label>
              <HintInput component="input" className={input} style={border} list="album-suggestions" value={batchMeta.album} onChange={(e) => setBatchMeta({ ...batchMeta, album: e.target.value })} data-testid="batch-album" hint="e.g. Cultana 2019" />
              <datalist id="album-suggestions">{albums.map((a) => <option key={a} value={a} />)}</datalist>
            </div>
            <div>
              <label className={label} style={{ color: 'var(--ink)' }}>Place / location name</label>
              <HintInput component="input" className={input} style={border} value={batchMeta.place} onChange={(e) => setBatchMeta({ ...batchMeta, place: e.target.value })} data-testid="batch-place" hint="e.g. Maria Island" />
            </div>
          </div>
          <div className="mt-3">
            <label className={label} style={{ color: 'var(--ink)' }}>Tags (applied to all)</label>
            <TagMultiSelect value={batchMeta.tags} onChange={(t) => setBatchMeta({ ...batchMeta, tags: t })} testid="batch-tags" />
          </div>
          <label className="flex items-center gap-2 text-sm mt-3" style={{ color: 'var(--ink)' }}>
            <input type="checkbox" checked={batchMeta.shared} onChange={(e) => setBatchMeta({ ...batchMeta, shared: e.target.checked })} data-testid="batch-share" />
            Share all with the site (goes to an admin for approval before appearing in the gallery)
          </label>
          <div className="flex gap-2 mt-4">
            <button onClick={saveBatch} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded text-sm font-title"
              style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: saving ? 0.7 : 1 }} data-testid="batch-save">
              {saving && <Loader2 size={14} className="animate-spin" />} Save {batchPhotos.length} photos
            </button>
            <button onClick={reset} className="px-3 py-2 rounded text-sm" style={{ border: '1px solid rgba(0,0,0,0.15)' }} data-testid="batch-cancel-secondary">Cancel</button>
          </div>
        </div>
      )}

      {stage === 'form' && photo && (
        <div className="mt-3 p-4 rounded-lg border" style={{ borderColor: 'rgba(0,0,0,0.12)', background: 'rgba(0,0,0,0.02)' }} data-testid="photo-meta-form">
          <div className="flex items-start justify-between gap-3 mb-3">
            <h4 className="font-title text-sm" style={{ color: 'var(--navy)' }}>Photo details</h4>
            <button onClick={reset} data-testid="photo-cancel"><X size={16} /></button>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            {preview && <img src={preview} alt="preview" className="w-full sm:w-40 h-40 object-cover rounded" style={{ border: '2px solid white' }} />}
            <div className="flex-1 space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <button type="button" onClick={runAnalyze} disabled={analyzing} data-testid="photo-analyze-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-title"
                  style={{ background: 'var(--gold)', color: '#3a2c06', opacity: analyzing ? 0.7 : 1 }}>
                  {analyzing ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  {analyzing ? 'Analysing…' : 'Analyse images'}
                </button>
                <span className="text-xs" style={{ color: 'var(--olive)' }}>Auto‑fills title, description &amp; tags — you can edit before saving.</span>
              </div>
              {aiSuggest && (
                <div className="text-xs p-2 rounded" style={{ background: 'rgba(201,161,59,0.12)', color: 'var(--ink)' }} data-testid="ai-suggest-note">
                  Detected {aiSuggest.people_count || 0} {(aiSuggest.people_count === 1) ? 'person' : 'people'}.
                  {aiSuggest.detected_text ? <> Text seen: <em>“{aiSuggest.detected_text}”</em>.</> : null}
                  {(aiSuggest.suggested_members && aiSuggest.suggested_members.length > 0) && (
                    <div className="mt-1">Suggested members: {aiSuggest.suggested_members.map((s) => s.name).join(', ')}</div>
                  )}
                </div>
              )}
              {(exif && (exif.camera || exif.taken_at)) && (
                <p className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--olive)' }} data-testid="exif-note">
                  <Info size={13} /> Detected{exif.camera ? ` ${exif.camera}` : ''}{exif.taken_at ? `, taken ${exif.taken_at}` : ''}.
                </p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={label} style={{ color: 'var(--ink)' }}>Title / caption</label>
                  <HintInput component="input" className={input} style={border} value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} data-testid="photo-title" hint="e.g. Bridging exercise, Derwent" />
                </div>
                <div>
                  <label className={label} style={{ color: 'var(--ink)' }}>Date taken</label>
                  <input type="date" className={input} style={border} value={meta.date_taken || ''} onChange={(e) => setMeta({ ...meta, date_taken: e.target.value })} data-testid="photo-date" />
                </div>
              </div>
              <div>
                <label className={label} style={{ color: 'var(--ink)' }}>Description</label>
                <textarea className={input} style={border} rows={2} value={meta.description} onChange={(e) => setMeta({ ...meta, description: e.target.value })} data-testid="photo-description" placeholder="Optional description" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={label} style={{ color: 'var(--ink)' }}>Place / location name</label>
                  <HintInput component="input" className={input} style={border} value={meta.place} onChange={(e) => setMeta({ ...meta, place: e.target.value })} data-testid="photo-place" hint="e.g. Maria Island" />
                </div>
                <div>
                  <label className={label} style={{ color: 'var(--ink)' }}>Album</label>
                  <HintInput component="input" className={input} style={border} list="album-suggestions" value={meta.album} onChange={(e) => setMeta({ ...meta, album: e.target.value })} data-testid="photo-album" hint="e.g. Cultana 2019" />
                  <datalist id="album-suggestions">{albums.map((a) => <option key={a} value={a} />)}</datalist>
                </div>
                <div>
                  <label className={label} style={{ color: 'var(--ink)' }}>People pictured</label>
                  <input className={input} style={border} value={meta.people} onChange={(e) => setMeta({ ...meta, people: e.target.value })} data-testid="photo-people" placeholder="Names (optional)" />
                </div>
              </div>
              <div>
                <label className={label} style={{ color: 'var(--ink)' }}>Tags</label>
                <TagMultiSelect value={meta.tags} onChange={(t) => setMeta({ ...meta, tags: t })} testid="photo-tags" />
              </div>

              {exif && exif.has_gps && (
                <label className="flex items-start gap-2 text-sm p-2 rounded" style={{ color: 'var(--ink)', background: 'rgba(201,161,59,0.12)' }}>
                  <input type="checkbox" className="mt-0.5" checked={meta.include_location} onChange={(e) => setMeta({ ...meta, include_location: e.target.checked })} data-testid="photo-include-location" />
                  <span className="flex items-center gap-1"><MapPin size={14} style={{ color: 'var(--maroon)' }} /> This photo has GPS location data. Include it? <em style={{ color: 'var(--olive)' }}>(off by default for privacy)</em></span>
                </label>
              )}

              <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--ink)' }}>
                <input type="checkbox" checked={meta.shared} onChange={(e) => setMeta({ ...meta, shared: e.target.checked })} data-testid="photo-share" />
                Share with the site (goes to an admin for approval before appearing in the gallery)
              </label>

              <div className="flex gap-2">
                <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded text-sm font-title"
                  style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: saving ? 0.7 : 1 }} data-testid="photo-save">
                  {saving && <Loader2 size={14} className="animate-spin" />} Save photo
                </button>
                <button onClick={reset} className="px-3 py-2 rounded text-sm" style={{ border: '1px solid rgba(0,0,0,0.15)' }} data-testid="photo-cancel-secondary">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
